import { ChevronRightIcon } from "@heroicons/react/24/solid";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import FormInput from "../../../components/controls/FormInput";
import FormTextArea from "../../../components/controls/FormTextArea";
import Button from "../../../components/controls/Button";

const jobs = [
  {
    id: 1,
    title: "Représentant.e",
    image:
      "https://qxidrfcclqstiadjauhz.supabase.co/storage/v1/object/public/makeadream-images/Page%20Accueil/Representant.png",
    code: "Expert en ventes et relations clients",
  },
  {
    id: 2,
    title: "Programmeur.se-Analyste fullstack",
    image:
      "https://qxidrfcclqstiadjauhz.supabase.co/storage/v1/object/public/makeadream-images/Page%20Accueil/RerchercheProgrammeur.png",
    code: "Maître de la transformation de café en code",
  },
  {
    id: 3,
    title: "Programmeur.se-Analyste fullstack + Représentant.e commercial",
    image:
      "https://qxidrfcclqstiadjauhz.supabase.co/storage/v1/object/public/makeadream-images/Page%20Accueil/ProgrammeurRepressentant.png",
    code: "Maître de la transformation de café en code et en ventes",
  },
  {
    id: 4,
    title: "Candidature spontanée",
    image:
      "https://qxidrfcclqstiadjauhz.supabase.co/storage/v1/object/sign/receptioncvs/candidsponta.jpg?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9iOTdhZjg3My00ZDY3LTRiZGItOWUxMy0xMWRhNDc0ODI2MGIiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJyZWNlcHRpb25jdnMvY2FuZGlkc3BvbnRhLmpwZyIsImlhdCI6MTc2NTEzODMxNCwiZXhwIjoxODI4MjEwMzE0fQ.ctyKtG02jw-dmn9tm-PFgCcyxCxnUTwJMUlD_V9_fYo",
    code: "Envoyez-nous votre CV",
  },
];

export default function CareersPage() {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(jobs[0]);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    motivation: "",
    notes: "",
    file: null,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const validateForm = () => {
    if (!form.firstName.trim() || !form.lastName.trim()) {
      alert(t("recruitment.errorName"));
      return false;
    }
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) {
      alert(t("recruitment.errorEmail"));
      return false;
    }
    if (!form.file || !form.file.type?.includes("pdf")) {
      alert(t("recruitment.errorFile"));
      return false;
    }
    return true;
  };

  const resetForm = () =>
    setForm({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      motivation: "",
      notes: "",
      file: null,
    });

  const submitForm = async () => {
    if (!validateForm()) return;
    setLoading(true);

    try {
      const data = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (value) data.append(key, value);
      });
      data.append("jobTitle", selected.title);

      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/JobApplication/apply`,
        { method: "POST", body: data }
      );

      if (!res.ok) throw new Error("submit failed");
      setSubmitted(true);
      resetForm();
    } catch {
      alert(t("errors.submission"));
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    const el = document.getElementById("apply-form");
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.pageYOffset - 110;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  const handleSelectJob = (job) => {
    setSelected(job);
    setSubmitted(false);
  };

  const renderFile = () => (
    <div className="w-full">
      <label className="block text-sm font-medium text-primary mb-1">
        {t("recruitment.cv")}
      </label>
      <div className="relative ">
        <input
          type="file"
          accept="application/pdf"
          onChange={(e) => handleChange("file", e.target.files?.[0] || null)}
          className="w-full h-12  bg-white text-primary  focus:outline-none"
        />
      </div>
    </div>
  );

  return (
    <div className="bg-gray-100 min-h-screen text-primary">
      <div>
        <h1 className="text-4xl font-bold mb-8 text-center text-gray-800 mt-8">
          {t("recruitment.title")}
        </h1>
        <h2 className="text-lg text-center text-gray-800 mb-8 md:w-1/2 w-full px-4 mx-auto">
          {t("recruitment.subtitle")}
        </h2>
        <div className="flex justify-center px-4 mb-4">
          <Button
            as="button"
            type="button"
            onClick={scrollToForm}
            color="primary"
            size="lg"
            className="w-full max-w-xs"
            rightIcon={<ChevronRightIcon className="w-5 h-5" />}
          >
            {t("recruitment.applyNow")}
          </Button>
        </div>
      </div>

      <div className="min-h-screen bg-accent p-8">
        <div className="flex flex-col md:flex-row gap-6">
          <div className="w-full md:w-1/3 flex flex-col gap-2">
            {jobs.map((job) => (
              <button
                key={job.id}
                type="button"
                onClick={() => handleSelectJob(job)}
                className={`w-full text-left px-4 py-3 rounded-lg shadow-sm transition ${
                  selected.id === job.id
                    ? "bg-[#FFD76A] text-primary"
                    : "bg-white hover:bg-gray-100 text-primary"
                }`}
              >
                <div className="font-semibold">{job.title}</div>
                <div
                  className={`text-sm italic ${
                    selected.id === job.id ? "text-primary/65" : "text-gray-600"
                  }`}
                >
                  {job.code}
                </div>
              </button>
            ))}
          </div>

          <div className="w-full md:flex-1 bg-white rounded-lg shadow flex items-center justify-center p-4">
            <img
              src={selected.image}
              alt={selected.title}
              className="w-full max-w-[520px] rounded-lg shadow object-cover"
            />
          </div>
        </div>
      </div>

      <div
        className="m-8 bg-white p-6 rounded-lg shadow-md max-w-3xl mx-auto text-primary"
        id="apply-form"
      >
        <h2 className="text-2xl font-bold mb-4 text-gray-800">
          {t("recruitment.applyFor")}{" "}
          <span className="text-primary">{selected.title}</span>
        </h2>

        {submitted ? (
          <p className="text-green-600 font-medium">
            {t("recruitment.success")}
          </p>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="w-full md:w-1/2">
                <FormInput
                  label={t("recruitment.firstName")}
                  name="firstName"
                  value={form.firstName}
                  onChange={(_, v) => handleChange("firstName", v)}
                  required
                />
              </div>
              <div className="w-full md:w-1/2">
                <FormInput
                  label={t("recruitment.lastName")}
                  name="lastName"
                  value={form.lastName}
                  onChange={(_, v) => handleChange("lastName", v)}
                  required
                />
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-4">
              <div className="w-full md:w-1/2">
                <FormInput
                  label={t("recruitment.email")}
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={(_, v) => handleChange("email", v)}
                  required
                />
              </div>
              <div className="w-full md:w-1/2">
                <FormInput
                  label={t("recruitment.phone")}
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={(_, v) => handleChange("phone", v)}
                  placeholder="418-555-0123"
                />
              </div>
            </div>

            <FormTextArea
              label={t("recruitment.motivation")}
              name="motivation"
              value={form.motivation}
              onChange={(_, v) => handleChange("motivation", v)}
              maxLength={800}
            />

            {renderFile()}

            <FormTextArea
              label={t("recruitment.notes")}
              name="notes"
              value={form.notes}
              onChange={(_, v) => handleChange("notes", v)}
              maxLength={800}
            />

            <div className="flex justify-center">
              <Button
                as="button"
                type="button"
                onClick={submitForm}
                color="primary"
                size="lg"
                disabled={loading}
                className="w-full md:w-auto"
              >
                {t("recruitment.submit")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
